/**
 * Upgrades a set of <select> elements into accessible custom dropdowns that
 * share "only one open at a time" behavior. Used by the homepage device
 * finder (custom-product-finder.liquid) and the in-collection device filter
 * picker (collection-device-filter.liquid).
 *
 * Expects each select's markup (see either section for the canonical shape):
 *   .finder__select-wrap
 *     select.finder__select
 *     button.finder__select-button > span.finder__select-text
 *     ul.finder__options
 *
 * @param {HTMLSelectElement[]} selects - Selects that close each other when one opens.
 * @param {Element} [container] - Outside click on this element closes all selects.
 */
function createProgressiveSelectGroup(selects, container) {
  function closeAll(except) {
    selects.forEach(function (select) {
      var wrap = select.closest('.finder__select-wrap');
      if (!wrap || wrap === except) return;

      var button = wrap.querySelector('.finder__select-button');
      var options = wrap.querySelector('.finder__options');
      wrap.classList.remove('is-open');
      if (button) button.setAttribute('aria-expanded', 'false');
      if (options) options.hidden = true;
    });
  }

  function syncSelectUi(select) {
    var wrap = select.closest('.finder__select-wrap');
    if (!wrap) return;

    var button = wrap.querySelector('.finder__select-button');
    var text = wrap.querySelector('.finder__select-text');
    var optionsList = wrap.querySelector('.finder__options');
    if (!button || !text || !optionsList) return;

    var selectedOption = select.options[select.selectedIndex] || select.options[0];
    text.textContent = selectedOption ? selectedOption.text : '';
    optionsList.innerHTML = '';

    Array.prototype.forEach.call(select.options, function (option) {
      if (option.value === '') return;

      var item = document.createElement('li');
      var optionButton = document.createElement('button');
      optionButton.type = 'button';
      optionButton.className = 'finder__option';
      optionButton.textContent = option.text;
      optionButton.dataset.value = option.value;

      if (option.value === select.value) {
        optionButton.classList.add('is-selected');
      }

      item.appendChild(optionButton);
      optionsList.appendChild(item);
    });

    button.disabled = select.disabled || optionsList.children.length === 0;
    if (button.disabled) closeAll();
  }

  function setPlaceholder(select, label) {
    select.innerHTML = '';
    select.add(new Option(label, ''));
    syncSelectUi(select);
  }

  function setLoading(select, label) {
    select.disabled = true;
    setPlaceholder(select, label);
  }

  function setError(select, label) {
    select.disabled = true;
    setPlaceholder(select, label);
  }

  function resetSelect(select, label, disabled) {
    setPlaceholder(select, label);
    select.disabled = disabled;
    syncSelectUi(select);
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string} label - Placeholder option text.
   * @param {Array} items - Raw items to populate from.
   * @param {function(*, number): {text: string, value: string, dataset?: object}} mapItem
   */
  function populate(select, label, items, mapItem) {
    setPlaceholder(select, label);

    items.forEach(function (item, index) {
      var mapped = mapItem(item, index);
      var option = new Option(mapped.text, mapped.value);
      if (mapped.dataset) {
        Object.keys(mapped.dataset).forEach(function (key) {
          option.dataset[key] = mapped.dataset[key];
        });
      }
      select.add(option);
    });

    select.disabled = items.length === 0;
    syncSelectUi(select);
  }

  function setupOne(select) {
    var wrap = select.closest('.finder__select-wrap');
    if (!wrap) return;

    var button = wrap.querySelector('.finder__select-button');
    var optionsList = wrap.querySelector('.finder__options');
    if (!button || !optionsList) return;

    button.addEventListener('click', function (event) {
      event.preventDefault();
      if (button.disabled) return;

      var willOpen = !wrap.classList.contains('is-open');
      closeAll(wrap);
      wrap.classList.toggle('is-open', willOpen);
      button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      optionsList.hidden = !willOpen;
    });

    optionsList.addEventListener('click', function (event) {
      var optionButton = event.target.closest('.finder__option');
      if (!optionButton) return;

      select.value = optionButton.dataset.value;
      syncSelectUi(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      closeAll();
    });

    select.addEventListener('change', function () {
      syncSelectUi(select);
    });

    syncSelectUi(select);
  }

  selects.forEach(setupOne);

  document.addEventListener('click', function (event) {
    if (container && !container.contains(event.target)) closeAll();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll();
  });

  return {
    syncSelectUi: syncSelectUi,
    closeAll: closeAll,
    setPlaceholder: setPlaceholder,
    setLoading: setLoading,
    setError: setError,
    resetSelect: resetSelect,
    populate: populate
  };
}

window.createProgressiveSelectGroup = createProgressiveSelectGroup;
